import React, { useEffect, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useIsNative } from '../utils/useIsNative';
import { isPlatform } from '../utils/isPlatform';
import { initCap } from '../utils/format';
import { isAutomatedTests } from '../utils/isAutomatedTests';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';
import { Swiper as SwiperProps } from 'swiper';
import { Mousewheel, Pagination } from 'swiper/modules';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { UserOnboardingState } from './UserOnboardingState';
import { useSubscription } from '@meraki-internal/state';
import { useWindowSize } from '../utils/useWindowSize';
import { MixPanelEventEmitter } from '../metrics/MixPanelEventEmitter';
import { Text } from '../components/Text';
import { AuthHandler } from '../auth/AuthHandler';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { HistoryViewModel } from '../support/HistoryViewModel';
import { ISmartStorageProvider, LocalStorageProvider } from '../support/LocalStorageProvider';
import { PushNotificationService } from '../push-notifications/PushNotificationService';
import { InnerhivePlusDialogViewModel } from '../innerhive-plus/InnerhivePlusDialogViewModel';
import { AlertPresenter } from '../AlertBinder';

import 'swiper/css';
import 'swiper/css/pagination';

interface IOnboardingSlide {
    heading: string;
    details: any;
    image: string;
    buttonLabel: string;
    skipLabel?: string;
    onSkip?: () => void;
    action?: () => Promise<boolean>;
    actionStatus?: string;
    nativeOnly?: boolean;
}

type IDoneHandler = ({ hide, trackingFrom }: { hide: boolean, trackingFrom: string }) => Promise<void>;

type ISignIn = ({ trackingFrom }: { trackingFrom: 'Welcome Create Account' | 'Welcome Existing Account' }) => Promise<void>;

const Buttons: React.FC<{slide: IOnboardingSlide, signIn?: ISignIn, progress: number, onDone: IDoneHandler}> = ({ slide, signIn, progress, onDone }) => {
    const swiper = useSwiper();
    const isNative = useIsNative();
    const onContinue = async ({ skipAction }: { skipAction?: boolean } = {}) => {
        if (!skipAction && slide.action) {
            await slide.action();
        }
        if (skipAction && slide.onSkip) {
            slide.onSkip();
        }
        if (progress === 1) {
            onDone({ hide: true, trackingFrom: slide.buttonLabel });
        } else {
            swiper.slideNext();
        }
    };
    const onTryWithoutAccount = async () => {
        onDone({ hide: true, trackingFrom: 'Try Without Account' });
    };
    const onCreateAccount = async () => {
        await onDone({ hide: false, trackingFrom: 'Create Account' });
        await signIn!({ trackingFrom: 'Welcome Create Account' });
    };
    const onSignIn = async () => {
        await signIn!({ trackingFrom: 'Welcome Existing Account'});
    };
    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', maxWidth: 400, textAlign: 'center'}}>
            {!isNative && progress > 0 &&
                <IonButton fill="clear" type="button" onClick={() => swiper.slidePrev()}>&lt; Back</IonButton>
            }
            {progress === 0 && signIn &&
                <IonButton fill="clear" type="button" onClick={onSignIn}>I already have an account</IonButton>
            }
            {(progress === 1 && signIn) ? (
                <>
                    <IonButton fill="clear" type="button" onClick={onTryWithoutAccount}>Try without account</IonButton>
                    <IonButton expand="block" style={{'--border-radius': '16px'}} onClick={onCreateAccount}>Create account</IonButton>
                </>
            ) : (
                <>
                    {slide.action &&
                        <IonButton fill="clear" type="button" onClick={() => onContinue({ skipAction: true })}>{slide.skipLabel || 'Skip for now'}</IonButton>
                    }
                    {!slide.action && slide.actionStatus &&
                        <div style={{fontSize: 14, padding: '10px 0'}}>{slide.actionStatus}</div>
                    }
                    <IonButton expand="block" style={{padding: '0 50px', '--border-radius': '16px'}} onClick={() => onContinue()}>{!slide.action && slide.actionStatus ? 'Continue' : slide.buttonLabel}</IonButton>

                </>
            )}
        </div>
    );
};

const PortraitContent: React.FC<{slide: IOnboardingSlide, onDone: IDoneHandler, progress: number, signIn?: ISignIn}> = ({ slide, onDone, progress, signIn }) => {
    return (
        <div style={{display: 'flex', flexDirection: 'column', width: '100%', height: '100%', backgroundColor: 'white'}}>
            <div style={{width: '100%', height: '50%', textAlign: 'center', backgroundColor: '#9CD6EC', paddingTop: 'calc(var(--ion-safe-area-top) - 10px)'}}>
                <img style={{height: '100%', objectFit: 'cover'}} src={`./assets/images/welcome/${slide.image}`} alt="Caring" />
            </div>
            <div style={{width: '100%', height: '50%', maxWidth: 500, margin: '0 auto', borderTop: '1px solid var(--ion-color-step-100)'}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: '100%', width: '100%', padding: '70px 40px 40px'}}>
                    <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: 24, fontWeight: 'bold'}}>{slide.heading}</div>
                        <div style={{padding: '20px 0 0'}}>
                            <Text type="detail">{slide.details}</Text>
                        </div>
                    </div>
                    <Buttons slide={slide} progress={progress} onDone={onDone} signIn={signIn} />
                </div>
            </div>
        </div>
    );
};

const LandscapeContent: React.FC<{slide: IOnboardingSlide, onDone: IDoneHandler, progress: number, signIn?: ISignIn }> = ({ slide, onDone, progress, signIn }) => {
    return (
        <div style={{width: '100%', height: '100%', display: 'flex', alignItems: 'center', backgroundColor: 'white'}}>
            <div style={{width: '50%', height: '100%', display: 'flex', alignItems: 'center', backgroundColor: '#9CD6EC', padding: 15}}>
                <img style={{width: '100%', maxWidth: 600, margin: '0 auto', objectFit: 'cover'}} src={`./assets/images/welcome/${slide.image}`} alt="Caring" />
            </div>
            <div style={{width: '50%', maxWidth: 500, margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', height: 350}}>
                <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 10}}>
                    <div style={{fontSize: 26, fontWeight: 'bold'}}>{slide.heading}</div>
                    <div style={{padding: '20px 40px 0', textAlign: 'center'}}>
                        <div style={{padding: '20px 40px 0'}}>
                            <Text type="detail">{slide.details}</Text>
                        </div>
                    </div>
                </div>
                <Buttons slide={slide} progress={progress} onDone={onDone} signIn={signIn} />
            </div>
        </div>
    );
};

export class WelcomeStorageProvider {
    completed: ISmartStorageProvider<boolean>;

    static inject = () => [LocalStorageProvider];
    constructor(storage: LocalStorageProvider) {
        this.completed = storage.getBooleanProvider('welcome-completed', { userNeutral: true });
    }
}

export const Welcome: React.FC = () => {
    const { width, height } = useWindowSize();
    const isLandscape = (width > height);

    const history = useInstance(HistoryViewModel);
    const mixpanel = useInstance(MixPanelEventEmitter);
    const alert = useInstance(AlertPresenter);

    const isNative = useIsNative();

    const principal = useInstance(AppPrincipal);
    const isAnonymous = isAnonymousViewer(principal);
    const authHandler = useInstance(AuthHandler);

    const onboarding = useInstance(UserOnboardingState);
    useSubscription(() => onboarding);

    const paywall = useInstance(InnerhivePlusDialogViewModel);
    useSubscription(() => paywall);

    const { completed } = useInstance(WelcomeStorageProvider);

    const [show, setShow] = useState(false);

    const pushNotificationService = useInstance(PushNotificationService);
    const [notificationPermission, setNotificationPermission] = useState('disabled');

    useEffect(() => {
        if (isNative) {
            pushNotificationService.checkPermissions().then(setNotificationPermission);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // TODO add tests for welcome slides
        if (isAutomatedTests()) {
            return;
        }
        // if signed in after completing anonymously, transfer flag from storage to dynamo
        if (!isAnonymous && completed.get()) {
            onboarding.completeStep('recording').then(completed.remove);
            return;
        }
        // don't show when deep linking anywhere other than home page
        if (!['/', '/caregiver'].includes(history.getCurrentLocation().pathname)) {
            return;
        }
        // don't show if paywall is triggering
        if (history.getCurrentSearchParams().has('paywallTrigger')) {
            return;
        }
        // don't show if flag set in local storage for anonymous user
        if (isAnonymous && completed.get()) {
            return;
        }
        // don't show if flag set in dynamo for signed-in user
        if (!isAnonymous && (!onboarding.isLoaded() || onboarding.isStepComplete('recording'))) {
            return;
        }
        // made it through all the checks, so show
        setShow(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onboarding.state?.loaded, onboarding.state?.steps.recording]);

    useEffect(() => {
        if (show) {
            mixpanel.track('Welcome Onboarding Opened');
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show]);

    if (!show) {
        return null;
    }

    const statusMsg = (permission: string) => {
        return (['granted', 'denied'].includes(permission) ? `Permission ${initCap(permission)}` : '');
    };

    const onDone: IDoneHandler = async ({ hide, trackingFrom }) => {
        if (hide) {
            setShow(false);
        }
        if (isAnonymous) {
            // if anonymous, set flag in local storage
            completed.set(true);
        } else {
            // if signed in, set flag in dynamo
            await onboarding.completeStep('recording');
        }
        mixpanel.track('Welcome Flow Completed', () => ({ By: trackingFrom }));
    };

    const notificationsAction = !['granted', 'denied', 'disabled'].includes(notificationPermission) ? async () => {
        mixpanel.track('Notification Dialog Viewed', () => ({ Location: 'welcome', Response: 'Yes' }));
        setNotificationPermission(await pushNotificationService.requestPermission());
        return true;
    } : undefined;

    const paywallAction = !paywall.hasUpgraded() ? async () => {
        if (isAnonymous) {
            // present an alert on android, similar to the one presented automatically by ios,
            // this gives the user an opportunity to back out if they swipe by mistake
            if (!isPlatform('ios')) {
                if ('continue' !== await alert.showAlertV2({
                    header: 'Please Create an Account',
                    message: 'This will enable you to start your free trial of Innerhive+.',
                    options: [{ id: 'cancel', role: 'cancel', label: 'Cancel' }, { id: 'continue', label: 'Continue' }]
                })) {
                    return false;
                }
            }
            await onDone({ hide: false, trackingFrom: 'Paywall' });
            try {
                await authHandler.login({ trackingFrom: 'Welcome Paywall', returnTo: `/caregiver?paywallTrigger=welcome` });
            } catch {
                return false;
            }
        } else {
            await paywall.resume({ triggerSource: 'welcome' });
        }
        return true;
    } : undefined;

    const signIn = async ({ trackingFrom }: { trackingFrom: 'Welcome Create Account' | 'Welcome Existing Account' }) => {
        await authHandler.login({ trackingFrom });
    };

    const slides: IOnboardingSlide[] = [
        {
            heading: 'Catch Every Word',
            details: <>Care conversations move fast.<br/><b>Innerhive</b> takes notes so<br/>you can stay present.</>,
            image: '1-conversations.webp',
            buttonLabel: 'Tell me more...'
        },
        {
            heading: 'Instant Note Summaries',
            details: <>No scribbling, just <b>tap record</b>.<br/>Get automatic highlights and next steps—<br/>easy-to-read and ready to share.</>,
            image: '2-notes.webp',
            buttonLabel: 'Sounds easy!'
        },
        {
            heading: 'Get Reminded to Record',
            details: <>Allow notifications for calendar reminders<br/>so you <b>never forget</b> to capture notes.</>,
            image: '3-notifications.webp',
            buttonLabel: 'Yes, turn on',
            skipLabel: 'Maybe later',
            onSkip: () => mixpanel.track('Notification Dialog Viewed', () => ({ Location: 'welcome', Response: 'Maybe later' })),
            action: notificationsAction,
            actionStatus: statusMsg(notificationPermission),
            nativeOnly: true
        },
        {
            heading: 'Unlock the Full Care Suite',
            details: <>Try <b>Innerhive+</b> for unlimited recording<br/>summaries, appointment tracking,<br/>and more care organization!</>,
            image: '4-paywall.webp',
            buttonLabel: 'Start free trial!',
            skipLabel: 'Continue without enhanced features',
            action: paywallAction,
            actionStatus: paywall.hasUpgraded() ? 'Already subscribed!' : '',
            nativeOnly: true
        },
        {
            heading: 'Care Together',
            details: <>Keep everyone in the loop with<br/>community updates and <b>share the</b><br/><b>care load</b> with task management.</>,
            image: '5-care.webp',
            buttonLabel: 'Let’s do it!'
        }
    ];

    const filteredSlides = slides.filter(s => !s.nativeOnly || isNative);

    const Content = isLandscape ? LandscapeContent : PortraitContent;

    // when android is rotated to landscape, the navbar doesn't move, so avoid an unnecessary white bar in this scnarios
    const paddingBottom = (isLandscape && isPlatform('android') ? undefined : 'var(--ion-safe-area-bottom)');

    let handlingAction = false;

    return (
        <div style={{position: 'absolute', width: '100%', height: '100%', paddingBottom}}>
            <Swiper
                style={{height: '100%', width: '100%'}}
                modules={[Pagination, Mousewheel]}
                slidesPerView={1}
                centeredSlides
                mousewheel
                pagination={{ clickable: true }}

                // on swipe next: if slide has an action, go back to old slide, perform action, then go to new slide
                onBeforeTransitionStart={async (swiper: SwiperProps) => {
                    if (swiper.swipeDirection === 'next' && !handlingAction) {
                        const index = swiper.activeIndex;
                        const slide = filteredSlides[index - 1];
                        if (slide?.action) {
                            try {
                                handlingAction = true;
                                swiper.slidePrev(0, false);
                                if (await slide.action()) {
                                    swiper.slideNext(0, false);
                                }
                            } finally {
                                handlingAction = false;
                            }
                        }
                    }
                }}

                onSlideChange={s => {
                    const event = (s.previousIndex < s.activeIndex ? 'Welcome Screen Advanced' : 'Welcome Screen Reversed');
                    mixpanel.track(event, () => ({
                        From: `${s.previousIndex + 1}: ${filteredSlides[s.previousIndex].heading}`,
                        To: `${s.activeIndex + 1}: ${filteredSlides[s.activeIndex].heading}}`
                    }));
                }}
            >
                {filteredSlides.map((slide, i) => (
                    <SwiperSlide key={`slide-${i}`} style={{width: '100', height: '100%'}}>
                        <Content
                            slide={slide}
                            onDone={onDone}
                            progress={i/(filteredSlides.length - 1)}
                            signIn={isAnonymous ? signIn : undefined}
                        />
                    </SwiperSlide>
                ))}
            </Swiper>
        </div>
    );
};
